import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import formConfigJson from '@/components/Organisms/CompanyDetailsForm/formConfig.json';
import { genericMessage, getErrorMessage } from 'src/Utils/messageUtils';

interface FieldConfig {
  name: string;
  type: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  tooltipDescription?: string;
  step?: number;
  buttonType?: string;
  metaData?: object;
}

interface SubmitFormPayload {
  formData: any;
  tempFileUrl?: string;
}

interface FieldState {
  [key: string]: any;
}

interface FieldErrors {
  [key: string]: string | undefined;
}

interface FormState {
  formConfig: FieldConfig[];
  metaData: object;
  fieldState: FieldState;
  fieldErrors: FieldErrors;
  currentStep: number;
  submitSuccess: boolean;
  isModalVisible: boolean;
  description: { heading: string; message: string; type: string } | null;
  pendingRoute: string | null;
  companyData: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: FormState = {
  formConfig: [],
  metaData: {},
  fieldState: {},
  fieldErrors: {},
  currentStep: 0,
  submitSuccess: false,
  isModalVisible: false,
  description: null,
  pendingRoute: null,
  companyData: null,
  loading: false,
  error: null,
};

const addCompanySlice = createSlice({
  name: 'addCompany',
  initialState,
  reducers: {
    fetchDraftDataRequest: (state, action: PayloadAction<string | null>) => {
      state.loading = true;
    },
    fetchDraftDataSuccess: (state, action) => {
      state.loading = false;
      state.fieldState = action.payload.fieldState;

      state.metaData = {
        ...state.metaData,
        ...action.payload.metaData,
      };
      state.description = getErrorMessage(action.payload.message);
    },
    fetchDraftDataFailure: (state, action: PayloadAction<{ heading: string; message: string }>) => {
      state.loading = false;
      state.error = action.payload;
      state.description = getErrorMessage(action.payload.message);
    },
    setFormConfig: (state, action: PayloadAction<FieldConfig[]>) => {
      state.formConfig = action.payload;
    },
    setFieldError: (state, action: PayloadAction<{ name: string; error?: string }>) => {
      const { name, error } = action.payload;
      state.fieldErrors[name] = error;
    },
    clearFieldError: (state, action: PayloadAction<{ name: string }>) => {
      const { name } = action.payload;
      delete state.fieldErrors[name];
    },
    resetFormState: (state) => {
      state.fieldState = {};
      state.fieldErrors = {};
      state.currentStep = 0;
      state.metaData = {};
    },
    setFieldState: (state, action: PayloadAction<{ name: string; value: any }>) => {
      const { name, value } = action.payload;
      state.fieldState[name] = { value, error: state.fieldState[name]?.error };
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    clearCurrentStepErrors: (state) => {
      const currentStep = state.currentStep;
      Object.keys(state.fieldErrors).forEach((fieldName) => {
        const fieldConfig = state.formConfig.find((step) => step.name === fieldName);
        if (fieldConfig && fieldConfig.step === currentStep) {
          delete state.fieldErrors[fieldName];
        }
      });
    },
    resetCurrentStepState: (state) => {
      const currentStep = state.currentStep;

      if (currentStep === 0) {
        localStorage.removeItem('company_id');
        localStorage.removeItem('vat');
        localStorage.removeItem('company_name');
      }

      const fieldsToClear = formConfigJson.slice(currentStep).flatMap((step) => step.fields.map((field) => field.name));

      fieldsToClear.forEach((fieldName) => {
        state.fieldState[fieldName] = '';
      });

      fieldsToClear.forEach((fieldName) => {
        delete state.fieldErrors[fieldName];
      });
    },
    submitFormRequest: (state, action: PayloadAction<any>) => {},
    submitFormSuccess: (state, action: PayloadAction<{ heading: string; message: string }>) => {
      localStorage.removeItem('blockNavigation');
      state.isModalVisible = true;
      state.submitSuccess = true;
      state.description = getErrorMessage(action.payload.message);
    },
    submitFormFailure: (state, action: PayloadAction<{ heading: string; message: string }>) => {
      state.isModalVisible = true;
      state.submitSuccess = false;
      state.description = {
        heading: action.payload.heading,
        message: action.payload.message,
        type: 'Error',
      };
    },
    closeModal: (state) => {
      state.isModalVisible = false;
      state.description = null;
    },
    setPendingRoute: (state, action: PayloadAction<string | null>) => {
      state.pendingRoute = action.payload;
    },
    fetchCompanyRequest: (state, action) => {
      state.loading = true;
      state.error = null;
    },
    fetchCompanySuccess: (state, action) => {
      state.loading = false;
      state.fieldState = action.payload.fieldState;

      state.metaData = {
        ...state.metaData,
        ...action.payload.metaData,
      };
      const errorMessage = getErrorMessage(action.payload.message);

      if (errorMessage.message !== genericMessage.message) {
        state.description = errorMessage;
      }
    },

    fetchCompanyFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.description = {
        heading: action.payload.heading,
        message: action.payload.message,
        type: 'Error',
      };
    },
    setDescription: (state, action: PayloadAction<{ heading: string; message: string; type: string } | null>) => {
      state.description = action.payload;
    },
  },
});

export const {
  setFormConfig,
  setFieldError,
  clearFieldError,
  resetFormState,
  setFieldState,
  setCurrentStep,
  clearCurrentStepErrors,
  resetCurrentStepState,
  fetchDraftDataRequest,
  fetchDraftDataSuccess,
  fetchDraftDataFailure,
  submitFormRequest,
  submitFormSuccess,
  submitFormFailure,
  closeModal,
  setPendingRoute,
  fetchCompanyRequest,
  fetchCompanySuccess,
  fetchCompanyFailure,
  setDescription,
} = addCompanySlice.actions;

export default addCompanySlice.reducer;
